import { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Card,
  InputBase,
  CircularProgress,
  Button,
  Tooltip,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import NavigationIcon from "@mui/icons-material/Navigation";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BoltIcon from "@mui/icons-material/Bolt";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import { getSocket } from "../../services/socketService";
import { tokens } from "../../theme";
import { battColor, battStatusLabel, battAriaLabel, battNeedsAttention } from "../../utils/battery";
import { lastRunRelative, lastRunExact } from "../../utils/lastRun";
import RobotPicture from "../../components/ui/RobotPicture";
import {
  LedgerCard,
  LedgerGrid,
  PageHead,
  ViewToggle,
  StatusChip as KitStatusChip,
  Chip as KitChip,
  Icon as KitIcon,
  pageContainerSx,
} from "../../components/ui";

// Robot statuses this page can show. `unassigned` is portal-specific (an admin
// hasn't assigned this robot to any company/user yet) and has no equivalent in
// the ported kit's STATUS_META, so it's layered on top rather than folded in.
const UNASSIGNED_META = { label: "Unassigned", tone: "muted", dot: "var(--bq-text3)" };

// Never overridden by live connectivity: a reachable faulty robot is still faulty, and
// an unassigned one is unusable regardless. Mirrors the backend's own LOCKED cases.
const LOCKED_STATUSES = ["error", "unassigned"];

// The backend only rewrites `status` on a connect/disconnect TRANSITION, so a robot
// already connected before the server started reads 'idle' forever. Infer liveness
// from traffic instead, falling back to the stored status for the first paint.
const resolveRobotStatus = (storedStatus, connected) => {
  if (LOCKED_STATUSES.includes(storedStatus)) return storedStatus;
  if (connected) return "active";
  return storedStatus;
};

const FILTERS = [
  ["all", "All"],
  ["active", "Active"],
  ["idle", "Idle"],
];

// The ported kit's chip, so status reads identically in both portals. Only
// `unassigned` needs a local fallback — it has no STATUS_META entry, since the
// admin fleet always has an owner backing every robot.
const StatusChip = ({ status }) => {
  if (status === "unassigned") {
    return (
      <KitChip
        label={UNASSIGNED_META.label}
        tone={UNASSIGNED_META.tone}
        dot={UNASSIGNED_META.dot}
      />
    );
  }
  return <KitStatusChip status={status} />;
};

StatusChip.propTypes = { status: PropTypes.string };

// Battery is only known from the live BATTERY_STATUS_UPDATE socket event
// (keyed by serialID) — it isn't part of the REST robot object. Show a bar
// when we have a reading, a muted dash otherwise.
const BattBar = ({ level, width = 48, charging = false }) => {
  if (level == null) return <Typography sx={{ fontSize: 12, color: tokens.text3 }}>—</Typography>;
  const displayLevel = Number(level.toFixed(1));
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      // Condition is carried visually by the bar's colour, which colour-blind
      // users cannot read. One sentence — level, condition, charging — makes it
      // available on hover, and as hidden text below for assistive tech.
      title={battAriaLabel(displayLevel, charging)}
    >
      <span className="bq-sr-only">{battAriaLabel(displayLevel, charging)}</span>
      <Box
        sx={{
          width,
          height: 5,
          borderRadius: "3px",
          background: tokens.surface3,
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: `${displayLevel}%`,
            height: "100%",
            borderRadius: "3px",
            background: battColor(displayLevel),
          }}
        />
      </Box>
      {/* Everything below is the visual half of the same sentence, so it is
          hidden from assistive tech to avoid announcing the level twice. */}
      <Typography
        aria-hidden="true"
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: tokens.text2,
          minWidth: 30,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {displayLevel}%
      </Typography>
      {/* Only Low/Critical get a visible word — "Good" on every healthy robot
          is noise. The hidden sentence above states it either way. */}
      {battNeedsAttention(displayLevel) && (
        <Typography
          aria-hidden="true"
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: battColor(displayLevel),
            textTransform: "uppercase",
            letterSpacing: ".04em",
          }}
        >
          {battStatusLabel(displayLevel)}
        </Typography>
      )}
      {charging && <BoltIcon aria-hidden="true" sx={{ fontSize: 14, color: tokens.warning }} />}
    </Stack>
  );
};

// Compact battery readout for the ledger row: bar + percentage only (no
// charging icon / status word — the table's BattBar has room for those, this
// column doesn't). Renders "—" for an unknown level so the row's height never
// depends on whether a live reading has arrived yet (see RobotCard below).
const LedgerBattery = ({ level, charging }) => {
  if (level == null) return <span style={{ color: "var(--bq-text3)" }}>—</span>;
  const displayLevel = Number(level.toFixed(1));
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      gap={0.75}
      title={battAriaLabel(displayLevel, charging)}
    >
      <span className="bq-sr-only">{battAriaLabel(displayLevel, charging)}</span>
      <Box
        aria-hidden="true"
        sx={{
          width: 28,
          height: 5,
          borderRadius: "3px",
          background: "var(--bq-surface3)",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: `${displayLevel}%`,
            height: "100%",
            borderRadius: "3px",
            background: battColor(displayLevel),
          }}
        />
      </Box>
      <span aria-hidden="true" style={{ fontVariantNumeric: "tabular-nums" }}>
        {displayLevel}%
      </span>
      {charging && (
        <BoltIcon aria-hidden="true" sx={{ fontSize: 13, color: "var(--bq-warning)" }} />
      )}
    </Stack>
  );
};
LedgerBattery.propTypes = { level: PropTypes.number, charging: PropTypes.bool };

const RobotCard = ({ robot, onClick, connected, battery, isCharging }) => {
  const mapName = robot.assignedMap?.name || "Not assigned";
  // Venue comes from the backend as a flat string, derived robot -> assignedMap ->
  // map.venueId (see shapeRobot in controller/portalRobots.controller.js). A robot
  // with no map has no venue, so it falls back exactly like the map field does.
  const venueName = robot.venueName || "Not assigned";
  const awaiting = !!robot.awaitingMapAssignment;
  const disabled = !!robot.disabled?.isDisabled;
  const serial = robot.serialNumber || "—";

  // Shared LedgerCard rows, so the grid matches botriq-admin-fe's Fleet cards.
  // `dim` mutes a value the admin has not set yet, so "Not assigned" reads as an
  // absent value rather than a real venue/map name.
  const ledgerIcon = (name) => <KitIcon name={name} size={11} color="var(--bq-text3)" />;
  const LEDGER_ROWS = [
    { key: "serial", label: "Serial", icon: ledgerIcon("hash"), value: serial, mono: true },
    {
      key: "venue",
      label: "Venue",
      icon: ledgerIcon("building"),
      value: venueName,
      dim: !robot.venueName,
    },
    {
      key: "map",
      label: "Map",
      icon: ledgerIcon("map"),
      value: mapName,
      dim: !robot.assignedMap?.name,
    },
    {
      key: "battery",
      label: "Battery",
      icon: ledgerIcon("battery-medium"),
      // Renders "—" itself when the level is unknown, so the row is always
      // present and every card in the grid stays the same height.
      value: <LedgerBattery level={battery} charging={isCharging} />,
    },
    {
      key: "lastRun",
      label: "Last Run",
      icon: ledgerIcon("clock"),
      // Relative only — the ledger is a fixed-width summary, and the exact
      // timestamp is one click away on the detail page.
      value: lastRunRelative(robot.lastRunAt) || "No runs yet",
      dim: !robot.lastRunAt,
    },
  ];

  // No map means the robot can't be dispatched. Tinted rather than given an inline
  // banner, which made this card taller than its siblings; the explanation moves to
  // the hover tooltip below.
  const card = (
    <LedgerCard
      onClick={onClick}
      tone={awaiting || disabled ? "danger" : undefined}
      style={disabled ? { opacity: 0.65 } : undefined}
      head={
        <Stack direction="row" alignItems="center" gap={1.25}>
          <RobotPicture
            robotId={robot._id}
            botTypeId={robot.deviceType?._id}
            name={robot.name}
            size={36}
            radius={9}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: 700,
                color: "var(--bq-text)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {robot.name}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "var(--bq-text3)" }}>
              {robot.type || robot.roverType || "Robot"}
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <StatusChip status={resolveRobotStatus(robot.status, connected)} />
            {disabled && <KitChip label="Disabled" tone="danger" />}
          </Stack>
        </Stack>
      }
      rows={LEDGER_ROWS}
    />
  );

  if (disabled) {
    return (
      <Tooltip
        title={
          robot.disabled?.reason
            ? `This robot is disabled: ${robot.disabled.reason}`
            : "This robot is disabled by your administrator."
        }
        arrow
        placement="top"
      >
        {card}
      </Tooltip>
    );
  }
  if (!awaiting) return card;
  return (
    <Tooltip
      title="No map assigned. Ask your administrator to assign a map before using this robot."
      arrow
      placement="top"
    >
      {card}
    </Tooltip>
  );
};

const RobotTable = ({ list, battBySerial, connBySerial, onOpen }) => {
  const th = (label, align) => (
    <Box
      component="th"
      sx={{
        textAlign: align || "left",
        px: 1.5,
        height: 36,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: ".05em",
        textTransform: "uppercase",
        color: tokens.text3,
        background: tokens.surface2,
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      {label}
    </Box>
  );
  return (
    <Card sx={{ overflow: "hidden" }}>
      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
          <Box component="thead">
            <Box component="tr">
              {th("Robot")}
              {th("Type")}
              {th("Status")}
              {th("Map")}
              {th("Battery")}
              {th("Last Run")}
              {th("")}
            </Box>
          </Box>
          <Box component="tbody">
            {list.map((r) => {
              const battEntry = r.serialID != null ? battBySerial[r.serialID] : null;
              const battery = battEntry?.level ?? null;
              const isCharging = battEntry?.charging ?? !!r.charging;
              return (
                <Box
                  component="tr"
                  key={r._id}
                  onClick={() => onOpen(r._id)}
                  sx={{
                    height: 46,
                    borderBottom: `1px solid ${tokens.border}`,
                    cursor: "pointer",
                    "&:hover": { background: tokens.surface2 },
                  }}
                >
                  <Box component="td" sx={{ px: 1.5 }}>
                    <Stack direction="row" alignItems="center" gap={1.1}>
                      <RobotPicture
                        robotId={r._id}
                        botTypeId={r.deviceType?._id}
                        name={r.name}
                        size={26}
                        radius={7}
                      />
                      <Box>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.text }}>
                          {r.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: tokens.text3,
                            fontFamily: "'JetBrains Mono',monospace",
                          }}
                        >
                          {r.serialNumber || "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                  <Box component="td" sx={{ px: 1.5, fontSize: 12.5, color: tokens.text2 }}>
                    {r.type || r.roverType || "—"}
                  </Box>
                  <Box component="td" sx={{ px: 1.5 }}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <StatusChip
                        status={resolveRobotStatus(r.status, connBySerial[String(r.serialID)])}
                      />
                      {r.disabled?.isDisabled && (
                        <Tooltip
                          title={
                            r.disabled.reason
                              ? `Disabled: ${r.disabled.reason}`
                              : "Disabled by your administrator"
                          }
                        >
                          <Chip
                            size="small"
                            label="Disabled"
                            sx={{
                              height: 22,
                              fontSize: 11,
                              fontWeight: 600,
                              color: tokens.danger,
                              background: tokens.dangerSoft,
                            }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                  <Box component="td" sx={{ px: 1.5, fontSize: 12.5, color: tokens.text2 }}>
                    {/* Same signal as the card's inline notice, condensed for the
                        dense table: a warning icon with the full message on hover. */}
                    {r.awaitingMapAssignment ? (
                      <Tooltip title="No map assigned. Ask your administrator to assign a map before using this robot.">
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <WarningAmberIcon sx={{ fontSize: 13, color: tokens.warning }} />
                          <Typography sx={{ fontSize: 12.5, color: tokens.warning }}>
                            Not assigned
                          </Typography>
                        </Stack>
                      </Tooltip>
                    ) : (
                      r.assignedMap?.name || "Not assigned"
                    )}
                  </Box>
                  <Box component="td" sx={{ px: 1.5 }}>
                    <BattBar level={battery} charging={isCharging} />
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.5,
                      fontSize: 12.5,
                      whiteSpace: "nowrap",
                      color: r.lastRunAt ? tokens.text2 : tokens.text3,
                    }}
                  >
                    {/* Exact timestamp on hover — the relative age alone gets
                        vague past a day, and the column is too narrow for both. */}
                    {r.lastRunAt ? (
                      <Tooltip title={lastRunExact(r.lastRunAt) || ""}>
                        <span>{lastRunRelative(r.lastRunAt)}</span>
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </Box>
                  <Box component="td" sx={{ px: 1.5 }}>
                    <ChevronRightIcon sx={{ fontSize: 18, color: tokens.text3 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

const MyRobots = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [robots, setRobots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Seeded once from ?status= so the Dashboard's "Active now" / "Inactive" KPI cards can
  // deep-link straight into the matching chip. Anything not in FILTERS falls back to
  // "all"; after mount the chips own this state as before.
  const [statusFilter, setStatusFilter] = useState(() => {
    const requested = searchParams.get("status");
    return FILTERS.some(([id]) => id === requested) ? requested : "all";
  });
  const [view, setView] = useState("grid");
  const [battBySerial, setBattBySerial] = useState({});
  // serialID -> live connectivity, inferred from robot traffic the same way
  // botriq-admin-fe's LocationTrackerPage does it.
  const [connBySerial, setConnBySerial] = useState({});

  const fetchRobots = useCallback(async () => {
    try {
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/robots`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setRobots(await res.json());
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRobots();
  }, [fetchRobots]);

  useEffect(() => {
    const socket = getSocket();
    const refresh = () => fetchRobots();
    // Capture live battery readings keyed by serialID (payload: { robotId, data: { batteryLevel } }).
    // Each entry is { level, charging }.
    const onBattery = (msg) => {
      const serial = msg?.robotId ?? msg?.data?.serialID;
      const level = msg?.data?.batteryLevel;
      if (serial != null && typeof level === "number") {
        setBattBySerial((prev) => ({ ...prev, [serial]: { ...prev[serial], level } }));
      }
    };
    // SENSOR_STATUS_UPDATE also carries a battery fraction (0-1) plus a charging
    // flag. Merge both into the same battBySerial map (0-100) that
    // BATTERY_STATUS_UPDATE feeds, so the table/grid battery reading and
    // charging indicator stay correct regardless of which event last arrived
    // for this bot.
    const onSensorStatus = (msg) => {
      const serial = msg?.serialID;
      const fraction = msg?.battery;
      const charging = msg?.charging;
      if (serial == null) return;
      setBattBySerial((prev) => ({
        ...prev,
        [serial]: {
          ...prev[serial],
          ...(typeof fraction === "number" ? { level: fraction * 100 } : {}),
          ...(typeof charging === "boolean" ? { charging } : {}),
        },
      }));
    };
    // Liveness is inferred from TRAFFIC, not only robot_connected: any pose/nav/
    // location packet proves the robot is online (as admin-fe's Location Tracker does).
    // serialIDs arrive as both strings and numbers, so every key is String()-normalised
    // — on the lookup side too, or the match silently fails.
    const markConnected = (msg) => {
      const serial = msg?.serialID ?? msg?.robotId ?? msg?.data?.serialID;
      if (serial == null) return;
      const key = String(serial);
      setConnBySerial((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    };
    const markDisconnected = (msg) => {
      const serial = msg?.serialID ?? msg?.robotId ?? msg?.data?.serialID;
      if (serial == null) return;
      setConnBySerial((prev) => ({ ...prev, [String(serial)]: false }));
    };

    // Robot events are ROOM-SCOPED server-side (emitToRobotRoom): a socket in no room
    // receives none of them. Re-emitted on every 'connect' — room membership is
    // per-connection and is lost on reconnect.
    const joinFleet = () => socket.emit("subscribe-fleet");
    socket.on("connect", joinFleet);
    if (socket.connected) joinFleet();

    socket.on("USER_ACCESS_UPDATED", refresh);
    socket.on("BATTERY_STATUS_UPDATE", onBattery);
    socket.on("BATTERY_STATUS_UPDATE", markConnected);
    socket.on("SENSOR_STATUS_UPDATE", onSensorStatus);
    socket.on("SENSOR_STATUS_UPDATE", markConnected);
    socket.on("ACTION_CURRENT_POSE", markConnected);
    socket.on("nav_progress", markConnected);
    socket.on("location_data", markConnected);
    socket.on("robot_connected", markConnected);
    socket.on("robot_disconnected", markDisconnected);
    socket.on("robot-status", markConnected);
    return () => {
      socket.off("connect", joinFleet);
      socket.off("USER_ACCESS_UPDATED", refresh);
      socket.off("BATTERY_STATUS_UPDATE", onBattery);
      socket.off("BATTERY_STATUS_UPDATE", markConnected);
      socket.off("SENSOR_STATUS_UPDATE", onSensorStatus);
      socket.off("SENSOR_STATUS_UPDATE", markConnected);
      socket.off("ACTION_CURRENT_POSE", markConnected);
      socket.off("nav_progress", markConnected);
      socket.off("location_data", markConnected);
      socket.off("robot_connected", markConnected);
      socket.off("robot_disconnected", markDisconnected);
      socket.off("robot-status", markConnected);
    };
  }, [fetchRobots]);

  const filtered = robots.filter((r) => {
    // Resolved status, not the raw stored field — the filter must agree with the chip
    // the cards render, or a bot showing "Active" would sit in the Idle bucket.
    if (
      statusFilter !== "all" &&
      resolveRobotStatus(r.status, connBySerial[String(r.serialID)]) !== statusFilter
    )
      return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return `${r.name} ${r.serialNumber} ${r.type || r.roverType || ""}`.toLowerCase().includes(q);
    }
    return true;
  });

  // Three-state body: empty-state, table view, then the card grid. Extracted
  // from a nested ternary in the JSX below; branch order and output are
  // unchanged.
  const renderRobotList = () => {
    if (filtered.length === 0) {
      return (
        <Card
          sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 6 }}
        >
          <SmartToyIcon sx={{ fontSize: 40, color: tokens.text3 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: tokens.text2 }}>
            {robots.length === 0 ? "No robots assigned" : "No robots match"}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.text3 }}>
            {robots.length === 0
              ? "Robots assigned by admin will appear here"
              : "Try a different search or filter"}
          </Typography>
          {robots.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setSearch("");
                setStatusFilter("all");
              }}
              sx={{ mt: 1, borderColor: tokens.border, color: tokens.text }}
            >
              Clear filters
            </Button>
          )}
        </Card>
      );
    }
    if (view === "list") {
      return (
        <RobotTable
          list={filtered}
          battBySerial={battBySerial}
          connBySerial={connBySerial}
          onOpen={(id) => navigate(`/robots/${id}`)}
        />
      );
    }
    return (
      // Same LedgerGrid botriq-admin-fe's Fleet page uses for its card grid, so
      // the column width and gutter are identical between the two portals.
      <LedgerGrid>
        {filtered.map((robot) => {
          const battEntry = robot.serialID != null ? battBySerial[robot.serialID] : null;
          return (
            <RobotCard
              key={robot._id}
              robot={robot}
              connected={connBySerial[String(robot.serialID)]}
              battery={battEntry?.level ?? null}
              isCharging={battEntry?.charging ?? !!robot.charging}
              onClick={() => navigate(`/robots/${robot._id}`)}
            />
          );
        })}
      </LedgerGrid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress size={32} sx={{ color: tokens.accent }} />
      </Box>
    );
  }

  return (
    <Box sx={pageContainerSx()}>
      {/* Same PageHead (icon badge + title/sub, right-aligned actions) as
          botriq-admin-fe's Fleet page, so the two list pages read as one system. */}
      <PageHead
        title="My Robots"
        sub={`${robots.length} robot${robots.length !== 1 ? "s" : ""} assigned to your account`}
        icon="bot"
        actions={
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              sx={{ borderColor: "var(--bq-border)", color: "var(--bq-text)" }}
              onClick={() => {
                const rows = [
                  ["Name", "Serial", "Type", "Status", "Map"],
                  ...robots.map((r) => [
                    r.name,
                    r.serialNumber || "",
                    r.type || r.roverType || "",
                    r.status || "",
                    r.assignedMap?.name || "",
                  ]),
                ];
                const csv = rows
                  .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
                  .join("\n");
                const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = "robots.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<NavigationIcon />}
              onClick={() => navigate("/robot-tracker")}
            >
              Dispatch
            </Button>
          </>
        }
      />

      {/* Toolbar: search + filters + view toggle */}
      <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap" sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 240,
            px: 1.4,
            height: 36,
            borderRadius: "9px",
            border: "1px solid var(--bq-border)",
            background: "var(--bq-surface2)",
          }}
        >
          <SearchIcon sx={{ fontSize: 16, color: "var(--bq-text3)" }} />
          <InputBase
            placeholder="Search robots…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ fontSize: 13, flex: 1 }}
          />
        </Box>

        <Stack
          direction="row"
          gap={0.5}
          sx={{
            background: "var(--bq-surface2)",
            border: "1px solid var(--bq-border)",
            borderRadius: "9px",
            p: "3px",
          }}
        >
          {FILTERS.map(([id, label]) => (
            <Box
              key={id}
              component="button"
              onClick={() => setStatusFilter(id)}
              sx={{
                padding: "5px 12px",
                fontSize: 12,
                fontWeight: 600,
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                background: statusFilter === id ? "var(--bq-surface)" : "transparent",
                color: statusFilter === id ? "var(--bq-text)" : "var(--bq-text2)",
                boxShadow: statusFilter === id ? "var(--bq-shadow)" : "none",
              }}
            >
              {label}
            </Box>
          ))}
        </Stack>

        <Box sx={{ ml: "auto" }}>
          <ViewToggle view={view} onChange={setView} />
        </Box>
      </Stack>

      {/* Body */}
      {renderRobotList()}
    </Box>
  );
};

const robotShape = PropTypes.shape({
  _id: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  roverType: PropTypes.string,
  status: PropTypes.string,
  serialNumber: PropTypes.string,
  serialID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignedMap: PropTypes.shape({ name: PropTypes.string }),
  awaitingMapAssignment: PropTypes.bool,
  // ISO timestamp of when this robot's last navigation/patrol ended; null when
  // no run has been recorded yet.
  lastRunAt: PropTypes.string,
  // Flat venue name derived server-side from assignedMap -> map.venueId; null when
  // the robot has no map (or no venue behind it).
  venueName: PropTypes.string,
  disabled: PropTypes.shape({
    isDisabled: PropTypes.bool,
    reason: PropTypes.string,
  }),
  // Populated bot type — used by RobotPicture to look up the type's default picture.
  deviceType: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    displayName: PropTypes.string,
  }),
});

BattBar.propTypes = {
  level: PropTypes.number,
  width: PropTypes.number,
  charging: PropTypes.bool,
};

RobotCard.propTypes = {
  robot: robotShape.isRequired,
  onClick: PropTypes.func,
  connected: PropTypes.bool,
  battery: PropTypes.number,
  isCharging: PropTypes.bool,
};

RobotTable.propTypes = {
  list: PropTypes.arrayOf(robotShape).isRequired,
  battBySerial: PropTypes.object.isRequired,
  connBySerial: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
};

export default MyRobots;
